import React, { useCallback, useState } from "react";
import { ChevronDown, FileText } from "lucide-react";
import { api } from "../api/client";
import { sendBody, sendButtonLabel, sendConfirmText, sendNote, showSend } from "../lib/esignSend";

/**
 * 2026-10-20 (esign_tracking) -- e-sign paperwork tracking.
 *
 * EsignChip: the queue row's chip, before anyone opens the card - complete,
 * or who the paperwork is still waiting on (`patient.esign`, from the list).
 * EsignPanel: the card's "Paperwork" dropdown, like Text history. Loads on
 * FIRST OPEN (the server asks the signing service about this card's unfinished
 * envelopes then), and shows each document and each party: signed or not, and
 * when. No links and no patient names are shown. 2026-10-21: a lien's Attorney
 * line also says where the attorney's link goes; office staff and admins can
 * send it to an address they type (AttorneyBox below). 2026-10-21
 * (esign_manual_send): office staff, admins and location logins can text the
 * patient the paperwork link for their next visit (SendBox below).
 */

const TONE = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  sky: "bg-sky-50 text-sky-800 border-sky-200",
  red: "bg-rose-50 text-rose-700 border-rose-200",
  slate: "bg-slate-50 text-slate-700 border-slate-200",
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export function EsignChip({ summary, testId }) {
  if (!summary) return null;
  return (
    <span
      data-testid={testId}
      title={summary.label}
      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap ${TONE[summary.tone] || TONE.slate}`}
    >
      {summary.label}
    </span>
  );
}

function PartyLine({ party }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5" data-testid={`esign-party-${party.role}`}>
      <span className={party.signed ? "text-emerald-700" : "text-slate-600"}>
        {party.signed ? "✓" : "○"} {party.label}
      </span>
      <span className={party.signed ? "text-slate-500" : "text-amber-700"}>
        {party.signed ? `signed ${fmtWhen(party.signed_at)}`.trim() : "not yet"}
      </span>
    </div>
  );
}

/**
 * 2026-10-21 (esign_attorney_push) -- under the lien's Attorney line: where the
 * attorney's link is going (or why nothing was sent), and for office staff and
 * admins (`attorney.can_push`, decided by the server) a box to send it to the
 * address they type, or to correct the address and send it again.
 */
function AttorneyBox({ patientId, docRef, attorney, onSent }) {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  if (!attorney || attorney.state === "signed") return null;
  const needs = attorney.state === "needs_email" || attorney.state === "follow_up";

  const startEdit = () => {
    setEmail(attorney.address || "");
    setNote(null);
    setEditing(true);
  };

  const send = async () => {
    setBusy(true);
    setNote(null);
    try {
      const res = await api.post(`/crm/patients/${patientId}/esign/${docRef}/attorney`, { email });
      setEditing(false);
      setNote({
        ok: true,
        text: `Sending to ${res.data.address} within 5 minutes.${res.data.replaced_link ? " The earlier link no longer works." : ""}`,
      });
      await onSent();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setNote({ ok: false, text: (d && d.message) || "That didn't go through. Refresh and try again." });
    } finally {
      setBusy(false);
    }
  };

  const sentLine = [
    attorney.address ? `${attorney.state === "queued" ? "To" : "Sent to"} ${attorney.address}` : "",
    attorney.source_label ? `(${attorney.source_label})` : "",
    attorney.sent_at ? `· ${fmtWhen(attorney.sent_at)}` : "",
    attorney.times_sent > 1 ? `· reminder ${attorney.times_sent - 1} sent` : "",
    attorney.opened_at ? `· opened ${fmtWhen(attorney.opened_at)}` : "",
  ].filter(Boolean).join(" ");

  return (
    <div className="ml-4 mt-0.5 mb-1" data-testid="esign-attorney">
      <div className={needs ? "text-rose-700" : "text-slate-500"} data-testid="esign-attorney-label">{attorney.label}</div>
      {sentLine && <div className="text-slate-500 break-all" data-testid="esign-attorney-sent">{sentLine}</div>}
      {attorney.can_push && !editing && (
        <button
          type="button"
          onClick={startEdit}
          className={`mt-1 px-2 py-0.5 rounded border text-[11px] font-semibold ${needs ? "border-rose-300 text-rose-700 hover:bg-rose-50" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
          data-testid="esign-attorney-edit"
        >
          {attorney.address ? "Change email / send again" : "Add email and send"}
        </button>
      )}
      {editing && (
        <div className="mt-1 space-y-1" data-testid="esign-attorney-editor">
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="attorney@lawfirm.com"
            className="w-full px-2 py-1 border border-slate-300 rounded text-xs"
            data-testid="esign-attorney-input"
            autoFocus
          />
          <p className="text-slate-500">
            The lien goes to this address within 5 minutes, and the reminders start over. Use the attorney's office email,
            not a lien company or referral service. Separate up to 3 addresses with commas.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || !email.trim()}
              onClick={send}
              className="px-2 py-0.5 rounded bg-slate-800 text-white text-[11px] font-semibold disabled:opacity-50"
              data-testid="esign-attorney-send"
            >
              {busy ? "Sending…" : "Send to attorney"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setEditing(false)}
              className="px-2 py-0.5 rounded border border-slate-300 text-[11px]"
              data-testid="esign-attorney-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {note && (
        <p className={note.ok ? "text-emerald-700 mt-1" : "text-rose-700 mt-1"} data-testid="esign-attorney-note">{note.text}</p>
      )}
    </div>
  );
}

/**
 * 2026-10-21 (esign_manual_send) -- "Send paperwork link": texts the patient the
 * e-sign paperwork for the visit named in the confirm (the server keeps every
 * rule: already signed, STOP, California only, 15 minutes' notice). An earlier
 * link that still works stops working. For a patient who signed the consent
 * forms but not the lien, it re-sends their link (it opens the lien), or says to
 * use the paper lien once that link has closed (Matt's #47 review). Shown while
 * `data.can_send` (decided by the server). Always asks first.
 */
function SendBox({ patientId, data, onSent }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const hint = sendNote(data);
  if (!showSend(data)) {
    if (note) return <p className={note.ok ? "mt-2 text-emerald-700" : "mt-2 text-rose-700"} data-testid="esign-send-note">{note.text}</p>;
    return hint ? <p className="mt-2 pt-2 border-t border-slate-100 text-amber-800" data-testid="esign-send-hint">{hint}</p> : null;
  }

  const send = async () => {
    if (!window.confirm(sendConfirmText(data))) return;
    setBusy(true);
    setNote(null);
    try {
      const res = await api.post(`/crm/patients/${patientId}/esign/send`, sendBody(data));
      setNote({ ok: true, text: res.data.message });
      await onSent();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setNote({ ok: false, text: (d && d.message) || "That didn't go through. Refresh and try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 pt-2 border-t border-slate-100" data-testid="esign-send">
      <button
        type="button"
        disabled={busy}
        onClick={send}
        className="px-2 py-0.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 text-[11px] font-semibold disabled:opacity-50"
        data-testid="esign-send-button"
      >
        {busy ? "Sending…" : sendButtonLabel(data)}
      </button>
      {note && (
        <p className={note.ok ? "text-emerald-700 mt-1" : "text-rose-700 mt-1"} data-testid="esign-send-note">{note.text}</p>
      )}
    </div>
  );
}

export function EsignPanel({ patient }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.get(`/crm/patients/${patient.id}/esign`);
      setData(res.data);
    } catch (e) {
      setError("Paperwork status couldn't load. Try again in a moment.");
    }
  }, [patient.id]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data) load();
  };

  const headSummary = data ? data.summary : patient.esign;
  const docs = data ? data.documents : [];
  const newestRefresh = docs.reduce((m, d) => (d.refreshed_at && d.refreshed_at > m ? d.refreshed_at : m), "");

  return (
    <div className="mt-2 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="esign-panel">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
        data-testid="esign-toggle"
      >
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
          <FileText size={14} className="text-slate-400" />
          Paperwork
          <span className="normal-case font-normal">
            <EsignChip summary={headSummary} testId="esign-summary" />
          </span>
        </span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 text-xs" data-testid="esign-body">
          {error && <p className="text-rose-700">{error}</p>}
          {!error && !data && <p className="text-slate-500">Loading…</p>}
          {data && docs.length === 0 && <p className="text-slate-500">No e-sign paperwork has been sent for this patient.</p>}
          {data && docs.map((d) => (
            <div key={`${d.envelope_ref}-${d.doc_kind}`} className="py-2 border-t border-slate-100" data-testid={`esign-doc-${d.doc_kind}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-800">
                  {d.label}{d.test_mode ? " (TEST)" : ""}
                </span>
                <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold ${TONE[d.tone] || TONE.slate}`}>
                  {d.status_label}
                </span>
              </div>
              <div className="text-slate-500 mt-0.5">
                {d.sent_at ? `Sent ${fmtWhen(d.sent_at)}` : "Sent"}
                {d.link_opened_at ? ` · opened ${fmtWhen(d.link_opened_at)}` : ""}
                {d.status !== "completed" && d.status !== "voided" && d.link_closes_at && !d.parties.some((p) => p.role === "patient" && p.signed)
                  ? ` · patient's link closes ${fmtWhen(d.link_closes_at)}` : ""}
                {d.completed_at ? ` · complete ${fmtWhen(d.completed_at)}` : ""}
              </div>
              {d.status !== "voided" && (
                <div className="mt-1 pl-1">
                  {d.parties.map((p) => (
                    <React.Fragment key={p.role}>
                      <PartyLine party={p} />
                      {p.role === "attorney" && d.attorney && (
                        <AttorneyBox patientId={patient.id} docRef={d.envelope_ref} attorney={d.attorney} onSent={load} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          ))}
          {data && <SendBox patientId={patient.id} data={data} onSent={load} />}
          {data && (
            <p className="mt-2 pt-2 border-t border-slate-100 text-slate-500" data-testid="esign-footer">
              {!data.configured
                ? "Live signing status isn't set up yet."
                : docs.some((d) => d.stale)
                  ? "Couldn't reach the signing service just now; showing the last known status."
                  : newestRefresh
                    ? `Checked ${fmtWhen(newestRefresh)}.`
                    : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default EsignPanel;
